<template>
  <div class="container mt-4">
    <div class="card">
      <div class="card-header">
        <h3>Editar Subcategoría</h3>
        <button class="btn btn-sm btn-outline-secondary" @click="goBack">
          <i class="fas fa-arrow-left"></i> Volver
        </button>
      </div>
      
      <div class="card-body">
        <form @submit.prevent="update">
          <div v-if="!loading">
            <div class="form-group mb-3">
              <label class="form-label">Nombre</label>
              <input
                type="text"
                class="form-control"
                v-model="updateSubCategory.name"
                required
              />
            </div>
            
            <div class="form-group mb-3">
              <label class="form-label">Temática</label>
              <select
                class="form-select form-control"
                v-model="updateSubCategory.topic"
                required
              >
                <option value="">Seleccione una temática</option>
                <option v-for="t in topics" :key="t.UUID" :value="t.UUID">
                  {{ t.name }}
                </option>
              </select>
            </div>
            
            <div class="form-group mb-3">
              <label class="form-label">Categoría</label>
              <select
                class="form-select form-control"
                v-model="updateSubCategory.category"
                required
                :disabled="!updateSubCategory.topic"
              >
                <option value="">Seleccione una categoría</option>
                <option v-for="c in categories" :key="c.UUID" :value="c.UUID">
                  {{ c.name }}
                </option>
              </select>
            </div>

            <div class="form-group mb-3">
              <label class="form-check">
                <input
                  class="form-check-input"
                  type="checkbox"
                  v-model="updateSubCategory.enabled"
                />
                <span class="form-check-label">Activado</span>
              </label>
            </div>
            
            <div class="d-flex justify-content-end gap-2">
              <button
                type="button"
                class="btn btn-secondary"
                @click="$router.push(backRoute)"
              >
                Cancelar
              </button>
              <button
                type="submit"
                class="btn btn-primary"
                :disabled="isSubmitting"
              >
                <span v-if="isSubmitting" class="spinner-border spinner-border-sm me-1"></span>
                {{ isSubmitting ? 'Guardando...' : 'Guardar' }}
              </button>
            </div>
          </div>
          
          <div v-else class="text-center py-5">
            <div class="spinner-border" role="status">
              <span class="visually-hidden">Loading...</span>
            </div>
            <div>Cargando información</div>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import SubCategoryService from "@/services/subcategory.service";
import TopicService from "@/services/topic.service";
import CategoryService from "@/services/category.service";
import Swal from "sweetalert2";

export default {
  name: "EditSubcategoryView",
  computed: {
    subcategoryId() {
      return this.$route.params.uuid;
    }
  },
  data() {
    return {
      updateSubCategory: {
        name: '',
        topic: '',
        category: '',
        enabled: false,
      },
      topics: [],
      categories: [],
      loading: true,
      isSubmitting: false,
    };
  },
  methods: {
    async fetchData() {
      this.loading = true;
      try {
        // Fetch subcategory data
        const subcatResponse = await SubCategoryService.getSubCategory(this.subcategoryId);
        const subcategory = subcatResponse.data;
        
        // Fetch all topics
        const topicsResponse = await TopicService.getAllTopics();
        this.topics = topicsResponse.data;
        
        // Set form values
        this.updateSubCategory = {
          name: subcategory.name,
          topic: subcategory.topic?.UUID || '',
          category: subcategory.category?.UUID || '',
          enabled: subcategory.enabled,
        };
        
        // Fetch categories if topic is set
        if (this.updateSubCategory.topic) {
          await this.getCategories();
        }
        
      } catch (error) {
        console.error("Error fetching data:", error);
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'No se pudo cargar la subcategoría',
        });
        this.$router.push({ 
          path: '/app/blog', 
          query: { tab: 'subcategories' } 
        });
      } finally {
        this.loading = false;
      }
    },

    async getCategories() {
      if (!this.updateSubCategory.topic) return;
      
      try {
        const response = await CategoryService.getAllCategories({
          topic: this.updateSubCategory.topic,
        });
        this.categories = response.data;
        
        // Reset category if it's not in the new list
        if (this.updateSubCategory.category && 
            !this.categories.some(c => c.UUID === this.updateSubCategory.category)) {
          this.updateSubCategory.category = '';
        }
      } catch (error) {
        console.error("Error fetching categories:", error);
      }
    },

    async update() {
      this.isSubmitting = true;
      
      try {
        const response = await SubCategoryService.updateSubCategory(
          this.subcategoryId,
          {
            name: this.updateSubCategory.name,
            topic: this.updateSubCategory.topic,
            category: this.updateSubCategory.category,
            enabled: this.updateSubCategory.enabled,
          }
        );
        
        if (response.status === 200) {
          Swal.fire({
            position: "top-end",
            icon: "success",
            title: "Cambios guardados correctamente",
            showConfirmButton: false,
            timer: 1500,
            backdrop: false,
            toast: true,
          });
          this.$router.push({ 
            path: '/app/blog', 
            query: { tab: 'subcategories' } 
          });
        }
      } catch (error) {
        console.error("Error updating subcategory:", error);
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'No se pudieron guardar los cambios',
        });
      } finally {
        this.isSubmitting = false;
      }
    },

    goBack() {
      this.$router.push({ 
        path: "/app/blog", 
        query: { tab: "subcategories" } 
      });
    },
  },
  watch: {
    'updateSubCategory.topic': {
      handler: function(newVal) {
        if (newVal) {
          this.getCategories();
        } else {
          this.categories = [];
          this.updateSubCategory.category = '';
        }
      }
    },
    '$route.params.id': {
      immediate: true,
      handler(newId) {
        if (newId && newId !== this.subcategoryId) {
          this.subcategoryId = newId;
          this.fetchData();  // Re-fetch data if ID changes
        }
      }
    },
  },
  created() {
    this.fetchData();
  }
};
</script>

<style scoped>
.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
</style>